import type { Caractere, Celula, MotivoColisao, Orientacao, PalavraCanonica } from '../tipos';
import { mesmaLetra } from '../lexico/alfabeto';
import type { IndiceLexico } from '../lexico/indice';
import type { Grade } from './grade';
import { celulaNoIndice, escalar, passo, perpendicular, somar } from './coordenadas';

/**
 * Regras de posicionamento e colisão (§9).
 *
 * Desde a spec 1.2, uma palavra pode cruzar mais de uma palavra confirmada,
 * aproveitando as letras que encontra pelo caminho. O que mantém isso são,
 * é a regra que substituiu a proibição anterior: **nenhuma sequência
 * perpendicular formada por acidente**. Toda letra nova encostada em algo
 * existente produz uma palavra na outra direção, e essa palavra precisa
 * existir no dicionário.
 *
 * As duas condições que sobraram fazem o resto do trabalho:
 *
 * - letra que cai sobre célula ocupada tem de ser a mesma letra;
 * - a palavra não encosta em outra na sua própria direção (respiro), o que
 *   leria as duas como uma palavra maior que ninguém escreveu.
 */

export type { MotivoColisao };

export type ResultadoColocacao =
  | { readonly ok: true }
  | {
      readonly ok: false;
      readonly motivo: MotivoColisao;
      readonly celula: Celula;
      /** Presente em `perpendicular-invalida`: a sequência que se formaria. */
      readonly formada?: string;
    };

export interface CruzamentoObrigatorio {
  readonly celula: Celula;
  /** Índice, dentro do texto sendo colocado, que deve cair em `celula`. */
  readonly indiceNoTexto: number;
}

/**
 * A palavra que se forma na perpendicular ao colocar `letra` em `celula`.
 *
 * Devolve `undefined` quando a letra não encosta em nada naquela direção —
 * o caso comum, em que não há nada a validar. Quando há vizinho, a sequência
 * vai do primeiro ao último ocupado, com a letra nova no meio.
 */
export function palavraPerpendicular(
  grade: Grade,
  celula: Celula,
  lateral: Celula,
  letra: Caractere,
): string | undefined {
  const antes = somar(celula, escalar(lateral, -1));
  const depois = somar(celula, lateral);
  if (!grade.ocupada(antes) && !grade.ocupada(depois)) return undefined;

  // Quantos passos ocupados para trás e para a frente a partir da célula nova.
  let recuo = 0;
  while (grade.ocupada(somar(celula, escalar(lateral, -(recuo + 1))))) recuo += 1;

  let avanco = 0;
  while (grade.ocupada(somar(celula, escalar(lateral, avanco + 1)))) avanco += 1;

  const partes: string[] = [];
  for (let passo = -recuo; passo <= avanco; passo += 1) {
    if (passo === 0) {
      partes.push(letra);
      continue;
    }
    partes.push(grade.caractereEm(somar(celula, escalar(lateral, passo))) ?? '');
  }

  return partes.join('');
}

/**
 * Verifica se `letras` cabe na grade a partir de `origem` (§9).
 *
 * `cruzamento`, quando informado, é a célula que a palavra **precisa**
 * atravessar — a âncora. As demais interseções são bem-vindas, desde que a
 * letra coincida e nada inválido se forme na perpendicular.
 */
export function verificarColocacao(
  grade: Grade,
  letras: readonly Caractere[],
  origem: Celula,
  orientacao: Orientacao,
  indice: IndiceLexico,
  cruzamento?: CruzamentoObrigatorio,
): ResultadoColocacao {
  const direcao = passo(orientacao);
  const lateral = passo(perpendicular(orientacao));

  for (let i = 0; i < letras.length; i += 1) {
    const letra = letras[i];
    if (letra === undefined) continue;

    const c = celulaNoIndice(origem, orientacao, i);

    if (cruzamento !== undefined && i === cruzamento.indiceNoTexto) {
      if (c.coluna !== cruzamento.celula.coluna || c.linha !== cruzamento.celula.linha) {
        return { ok: false, motivo: 'cruzamento-fora-de-lugar', celula: c };
      }
    }

    const existente = grade.caractereEm(c);
    if (existente !== undefined) {
      // Célula já escrita: só passa se for exatamente a mesma letra. É aqui
      // que a palavra "aproveita" o que já está no tabuleiro.
      if (!mesmaLetra(existente, letra)) {
        return { ok: false, motivo: 'letra-diferente-no-cruzamento', celula: c };
      }
      // Cruzar é atravessar na perpendicular. Andar por cima de uma palavra
      // que corre na mesma direção não é cruzamento, é sobreposição — e o
      // respiro das pontas não a pega quando uma contém a outra.
      if (grade.temOrientacao(c, orientacao)) {
        return { ok: false, motivo: 'sobreposicao-paralela', celula: c };
      }
      continue;
    }

    // Célula nova encostada em algo: a sequência que nasce na outra direção
    // precisa ser palavra de verdade.
    const formada = palavraPerpendicular(grade, c, lateral, letra);
    if (formada !== undefined && !indice.existe(formada as PalavraCanonica)) {
      return { ok: false, motivo: 'perpendicular-invalida', celula: c, formada };
    }
  }

  // Respiro nas extremidades: encostar em outra palavra na própria direção
  // leria as duas como uma palavra maior, que ninguém escreveu (§9).
  const antesDoInicio = somar(origem, escalar(direcao, -1));
  const depoisDoFim = somar(origem, escalar(direcao, letras.length));
  if (grade.ocupada(antesDoInicio)) {
    return { ok: false, motivo: 'sem-respiro', celula: antesDoInicio };
  }
  if (grade.ocupada(depoisDoFim)) {
    return { ok: false, motivo: 'sem-respiro', celula: depoisDoFim };
  }

  return { ok: true };
}

export function podeColocar(
  grade: Grade,
  letras: readonly Caractere[],
  origem: Celula,
  orientacao: Orientacao,
  indice: IndiceLexico,
  cruzamento?: CruzamentoObrigatorio,
): boolean {
  return verificarColocacao(grade, letras, origem, orientacao, indice, cruzamento).ok;
}
